import constants from "./constants";
import globalState from "../utils/globalState";

const getDynamicParams = () => {
  const params = globalState.getAllUserParams();
  return {
    brokerage: params.brokerage || constants.BROKERAGE,
    agentNumber: params.agentNumber || constants.AGENT_NUMBER,
    agentEmail: params.agentEmail || constants.AGENT_EMAIL,
  };
};

// Get all user parameters
const getAllUserParams = () => {
  return globalState.getAllUserParams();
};

// Get current user's agentName
const getCurrentAgentName = () => {
  const params = getAllUserParams();
  return params.agentName;
};

const pageSEO = {
  // Home page SEO
  home: () => {
    const { brokerage, agentNumber } = getDynamicParams();
    return {
      title: "Home",
      description: `${brokerage} - Professional real estate services to help you find your dream home. We offer property search, market analysis, and expert realtor services.`,
      keywords:
        "real estate, realtor, property, home, house, real estate services",
      path: "/",
      structuredData: {
        "@context": "https://schema.org",
        "@type": "Organization",
        name: brokerage,
        url:
          constants.ENVIRONMENT === "prod"
            ? `https://${constants.DOMAIN}`
            : `http://${constants.URL}`,
        logo: "/logo.png",
        contactPoint: {
          "@type": "ContactPoint",
          telephone: agentNumber,
          contactType: "customer service",
        },
      },
    };
  },

  // Calculator page SEO
  calculator: () => ({
    title: "Mortgage Calculator",
    description:
      "Calculate your mortgage payments, affordability, and get personalized real estate financing insights.",
    keywords:
      "mortgage calculator, real estate financing, mortgage payments, affordability calculator",
    path: "/calculator",
  }),

  // Listings page SEO
  listings: () => ({
    title: "Property Listings",
    description:
      "Browse our curated selection of properties, including condos, single-family homes, and townhouses.",
    keywords: "property listings, real estate, homes for sale, condos, houses",
    path: "/listings",
  }),

  // Listing detail page SEO (dynamic)
  listing: (listing) => ({
    // title: `${listing.address} - ${listing.location}`,
    title: `${listing.StreetNumber} ${listing.StreetName} ${listing.StreetSuffix} - ${listing.City} ${listing.StateOrProvince} | ${getCurrentAgentName()}`,
    description: `${listing.bedrooms} bedroom ${listing.bathrooms} bathroom ${listing.propertyType} located in ${listing.location}, priced at ${listing.price}.`,
    keywords: `${listing.location}, ${listing.propertyType}, ${listing.bedrooms} bedroom, ${listing.bathrooms} bathroom, property`,
    path: `/listing/${listing.id}`,
    image: listing.mainImage,
    type: "product",
    structuredData: {
      "@context": "https://schema.org",
      "@type": "RealEstateListing",
      name: listing.address,
      description: listing.description,
      image: listing.mainImage,
      offers: {
        "@type": "Offer",
        priceCurrency: "CAD",
        price: listing?.price?.replace(/[^\d.]/g, ""),
        availability: "https://schema.org/InStock",
      },
    },
  }),

  // About page SEO
  about: () => {
    const { brokerage } = getDynamicParams();
    return {
      title: "About Us",
      description: `Learn about ${brokerage}'s team of experienced real estate professionals and our commitment to exceptional service.`,
      keywords:
        "about us, real estate team, real estate professionals, real estate company",
      path: "/about",
    };
  },

  // Evaluation page SEO
  evaluation: () => ({
    title: "Property Evaluation",
    description:
      "Get a free professional property evaluation to understand your home's current market value.",
    keywords:
      "property evaluation, home value, market analysis, real estate valuation",
    path: "/evaluation",
  }),

  // Reports page SEO
  reports: () => ({
    title: "Real Estate Reports",
    description:
      "Access comprehensive real estate market reports and insights for informed property decisions.",
    keywords:
      "real estate reports, market analysis, property insights, real estate data",
    path: "/real-estate-reports",
  }),

  // Contact page SEO
  contact: () => {
    const { brokerage } = getDynamicParams();
    return {
      title: "Contact Us",
      description: `Get in touch with ${brokerage}'s real estate professionals for personalized assistance.`,
      keywords:
        "contact real estate agent, real estate contact, property consultation",
      path: "/contact",
    };
  },

  // Agents page SEO
  agents: () => ({
    title: "Our Agents",
    description:
      "Meet our team of dedicated real estate professionals ready to help you with your property needs.",
    keywords: "real estate agents, real estate professionals, property experts",
    path: "/agents",
  }),

  // Commercial listings page SEO
  commercial: () => ({
    title: "Commercial Properties",
    description:
      "Explore our selection of commercial real estate properties and investment opportunities.",
    keywords:
      "commercial real estate, commercial properties, business properties, investment properties",
    path: "/commercial",
  }),

  // Seller's guide page SEO
  sellerGuide: () => ({
    title: "Seller's Guide",
    description:
      "Comprehensive guide to selling your property with expert tips and market insights.",
    keywords:
      "selling guide, home selling tips, property selling advice, real estate selling",
    path: "/selling-guide",
  }),

  // Buyer's guide page SEO
  buyerGuide: () => ({
    title: "Buyer's Guide",
    description:
      "Essential guide for home buyers with expert advice and property search tips.",
    keywords:
      "buying guide, home buying tips, property buying advice, real estate buying",
    path: "/buying-guide",
  }),

  // Location page SEO (dynamic)
  location: (location) => ({
    title: `Real Estate in ${location.name}`,
    description: `Find properties and real estate opportunities in ${location.name}. Expert local market insights and property listings.`,
    keywords: `${location.name} real estate, ${location.name} properties, ${location.name} homes`,
    path: `/location/${location.id}`,
  }),
};

export default pageSEO;
